import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Snowflake, Ban, CheckCircle2, Trash2, Loader2, Pencil,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  qrtGetUser, qrtSetUserStatus, qrtDeleteUser, qrtUserTransactions,
  qrtSetWalletFrozen, qrtAdjustWallet, qrtSetUserMarkups, qrtUpdateUser,
  qrtListCampaigns, qrtListFlows, qrtListTemplates, qrtListMessages,
  qrtGetUserAccess, qrtSetUserExempt, qrtGrantSubscription, qrtCancelSubscription, qrtListPlans,
  qrtSetExtraNumbers,
} from "../../apis/qrtAdmin";
import QrtUserInvoices from "./QrtUserInvoices";
import { Pager, Spinner, StatCard, StatusChip, fmtDateTime, fmtMoney, num } from "./qrtUi";
import { isCredit, parseMeta } from "../../utils/walletTxn";
import { grantPeriodsForPlan, periodLabel, catalogVersion } from "../../utils/planPricing";

const CATEGORIES = ["marketing", "marketing_lite", "utility", "authentication", "service"];
const CATEGORY_LABELS = {
  marketing: "Marketing", marketing_lite: "Marketing Lite", utility: "Utility",
  authentication: "Authentication", service: "Service",
};

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "access", label: "Access" },
  { id: "campaigns", label: "Campaigns" },
  { id: "flows", label: "Flows" },
  { id: "templates", label: "Templates" },
  { id: "messages", label: "Messages" },
  { id: "billing", label: "Billing" },
  { id: "invoices", label: "Invoice" },
];

function roundMoney(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function creditQuote(base, includeFee) {
  const gst = roundMoney(base * 0.18);
  const fee = includeFee ? roundMoney(base * 0.02) : 0;
  const gstOnFee = includeFee ? roundMoney(fee * 0.18) : 0;
  return { gst, fee, gstOnFee, total: roundMoney(base + gst + fee + gstOnFee) };
}

function inr(n) {
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function FeeChoice({ amount, busy, onCancel, onChoose }) {
  const withFee = creditQuote(amount, true);
  const withoutFee = creditQuote(amount, false);
  return (
    <>
      <div className="ping-drawer-backdrop" onClick={onCancel} />
      <div className="qrt-fee-ask" role="dialog" aria-modal="true" aria-label="Platform fee">
        <h3>Add the 2% platform fee?</h3>
        <p>
          {inr(amount)} is credited to the wallet either way. The invoice always includes 18% GST ({inr(withFee.gst)}).
        </p>
        <div className="qrt-fee-opts">
          <button type="button" disabled={busy} onClick={() => onChoose(true)}>
            <b>Include 2%</b>
            <span>Platform fee {inr(withFee.fee)}</span>
            <span>GST on that fee {inr(withFee.gstOnFee)}</span>
            <strong>Invoice {inr(withFee.total)}</strong>
          </button>
          <button type="button" disabled={busy} onClick={() => onChoose(false)}>
            <b>No platform fee</b>
            <span>Platform fee {inr(0)}</span>
            <span>GST on that fee {inr(0)}</span>
            <strong>Invoice {inr(withoutFee.total)}</strong>
          </button>
        </div>
        <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  );
}

/* Subscription access: exemption toggle, manual plan grant, history */
const AccessSection = ({ userId }) => {
  const [access, setAccess] = useState(null);
  const [plans, setPlans] = useState([]);
  const [grant, setGrant] = useState({ plan_id: "", billing_period: "monthly" });
  const [extraSlots, setExtraSlots] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    qrtGetUserAccess(userId)
      .then((res) => {
        const data = res.data?.data || null;
        setAccess(data);
        if (data) setExtraSlots(String(data.extra_numbers ?? 0));
      })
      .catch(() => toast.error("Failed to load access info"));
    qrtListPlans()
      .then((res) => setPlans((res.data?.data || []).filter((p) => p.is_active === 1 || p.is_active === true)))
      .catch(() => {});
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (!access) return <Spinner />;

  const current = access.current_subscription;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const limit = access.number_limit;
  const planName = current?.plan_display_name || limit?.planDisplayName || "No plan";
  const atCap = limit && Number(limit.current) >= Number(limit.maxNumbers);
  const addonBase = limit?.addonBase || 150;

  return (
    <>
      <div className="qrt-card">
        <div className="qrt-card-head"><h3>Subscription access</h3></div>
        <div className="qrt-card-body">
          <div className="qrt-access-grid">
            <div className="qrt-sect">
              <p className="t">Current plan</p>
              {current ? (
                <>
                  <div className="qrt-access-plan">
                    <b>{current.plan_display_name}</b>
                    {current.autopay_status && current.autopay_status !== "none" && (
                      <span className={`ping-chip ${current.autopay_status === "active" || current.autopay_status === "authenticated" ? "g" : current.autopay_status === "halted" ? "r" : "n"}`}>
                        auto-pay {current.autopay_status}
                      </span>
                    )}
                  </div>
                  <p className="qrt-access-note">
                    {periodLabel(current.billing_period)} · expires {fmtDateTime(current.expires_at)}
                  </p>
                  <button
                    type="button"
                    className="ping-btn ping-btn-ghost ping-btn-sm"
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Cancel this user's active subscription?")) return;
                      act(() => qrtCancelSubscription(userId), "Subscription cancelled");
                    }}
                  >
                    Cancel subscription
                  </button>
                </>
              ) : (
                <p className="qrt-access-note" style={{ marginBottom: 0 }}>No active subscription</p>
              )}
            </div>

            <div className="qrt-sect">
              <p className="t">Free access</p>
              <div className="qrt-access-plan">
                <span className={`ping-chip ${access.exempt ? "g" : "n"}`}>
                  {access.exempt ? "Granted" : "Off"}
                </span>
              </div>
              <p className="qrt-access-note">
                Lets this user use the panel without a paid plan.
              </p>
              <button
                type="button"
                className={`ping-btn ping-btn-sm ${access.exempt ? "ping-btn-ghost" : "ping-btn-green"}`}
                disabled={busy}
                onClick={() => act(
                  () => qrtSetUserExempt(userId, !access.exempt),
                  access.exempt ? "Free access revoked" : "Free access granted — user can use the panel without a plan"
                )}
              >
                {access.exempt ? "Revoke free access" : "Grant free access"}
              </button>
            </div>
          </div>

          <div className="qrt-sect">
            <p className="t">WhatsApp number adds</p>
            <p className="qrt-access-note">
              {planName} includes {limit ? limit.planMax : "—"} number add{Number(limit?.planMax) === 1 ? "" : "s"} per billing cycle.
              Disconnecting a number does not free a slot until the plan renews. Extra add-on is ₹{addonBase}+GST.
            </p>
            {limit ? (
              <div className="qrt-stats" style={{ marginBottom: 12 }}>
                <StatCard
                  label="Adds this cycle"
                  value={`${limit.current} / ${limit.maxNumbers}`}
                  sub={atCap ? "At cap this cycle" : `${Math.max(0, limit.remaining)} remaining this cycle`}
                  tone={atCap ? "warn" : ""}
                />
                <StatCard
                  label="Connected now"
                  value={limit.connected ?? "—"}
                  sub="Currently linked numbers"
                />
                <StatCard
                  label="Allowed total"
                  value={limit.maxNumbers}
                  sub="Plan + purchased + QRT extra"
                />
              </div>
            ) : (
              <p className="qrt-access-note">Number quota is not available for this user yet.</p>
            )}
            <div className="qrt-access-breakdown">
              <div>
                <span>Plan included</span>
                <b>{limit?.planMax ?? "—"}</b>
              </div>
              <div>
                <span>Purchased</span>
                <b>{limit?.purchasedSlots ?? 0}</b>
              </div>
              <div>
                <span>QRT extra</span>
                <b>{limit?.extraNumbers ?? 0}</b>
              </div>
            </div>
            <div className="qrt-access-extra">
              <label htmlFor="qrt-extra-slots">
                Free extra slots
                <span>QRT grant on top of the plan cap — not a paid add-on</span>
              </label>
              <div className="qrt-inline">
                <input
                  id="qrt-extra-slots"
                  type="number"
                  min="0"
                  value={extraSlots}
                  onChange={(e) => setExtraSlots(e.target.value)}
                  disabled={busy}
                />
                <button
                  type="button"
                  className="ping-btn ping-btn-green ping-btn-sm"
                  disabled={busy}
                  onClick={() => act(
                    () => qrtSetExtraNumbers(userId, Number(extraSlots || 0)),
                    "Extra WhatsApp number slots saved"
                  )}
                >
                  Save extra slots
                </button>
              </div>
            </div>
          </div>

          <div className="qrt-sect" style={{ marginBottom: 0 }}>
            <p className="t">Grant a plan</p>
            <p className="qrt-access-note">Manual grant — no payment. Includes the plan’s AI credits.</p>
            <div className="qrt-inline">
              <select
                value={grant.plan_id}
                onChange={(e) => {
                  const plan = plans.find((p) => String(p.id) === e.target.value);
                  const periods = grantPeriodsForPlan(plan);
                  setGrant({ plan_id: e.target.value, billing_period: periods[0]?.value || "monthly" });
                }}
              >
                <option value="">Choose a plan…</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.plan_display_name} ({catalogVersion(p) === 2 ? "new" : "legacy"})
                  </option>
                ))}
              </select>
              <select value={grant.billing_period} onChange={(e) => setGrant((g) => ({ ...g, billing_period: e.target.value }))}>
                {grantPeriodsForPlan(plans.find((p) => String(p.id) === String(grant.plan_id))).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="ping-btn ping-btn-green ping-btn-sm"
                disabled={busy || !grant.plan_id}
                onClick={() => act(async () => {
                  await qrtGrantSubscription(userId, { plan_id: Number(grant.plan_id), billing_period: grant.billing_period });
                  setGrant({ plan_id: "", billing_period: "monthly" });
                }, "Plan granted (audit-logged, includes plan AI credits)")}
              >
                Grant plan
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="qrt-card">
        <div className="qrt-card-head"><h3>Subscription history</h3></div>
        <table className="qrt-table">
          <thead>
            <tr><th>Plan</th><th>Billing</th><th>Status</th><th className="r">Expires</th><th className="r">Created</th></tr>
          </thead>
          <tbody>
            {(access.history || []).map((s) => (
              <tr key={s.id}>
                <td><b>{s.plan_display_name}</b> {!s.payment_id && <span className="ping-chip n">manual</span>}</td>
                <td className="mono">{periodLabel(s.billing_period)}</td>
                <td><StatusChip value={s.status} /></td>
                <td className="r sub">{fmtDateTime(s.expires_at)}</td>
                <td className="r sub">{fmtDateTime(s.created_at)}</td>
              </tr>
            ))}
            {(access.history || []).length === 0 && (
              <tr><td className="empty" colSpan={5}>No subscriptions yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="qrt-card">
        <div className="qrt-card-head"><h3>Platform auto-pay charges</h3></div>
        <table className="qrt-table">
          <thead>
            <tr>
              <th>Payment</th>
              <th>Billing</th>
              <th className="r">Base</th>
              <th className="r">Paid</th>
              <th className="r">Period end</th>
            </tr>
          </thead>
          <tbody>
            {(access.charges || []).map((c) => (
              <tr key={c.id}>
                <td className="mono">{c.razorpay_payment_id}</td>
                <td className="mono">{periodLabel(c.billing_period)}</td>
                <td className="r">{fmtMoney(c.base_amount)}</td>
                <td className="r">{fmtMoney(c.amount)}</td>
                <td className="r sub">{fmtDateTime(c.period_end)}</td>
              </tr>
            ))}
            {!(access.charges || []).length && (
              <tr><td className="empty" colSpan={5}>No auto-pay charges yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

const UserList = ({ loader, columns, renderRow, empty, pageSize = 25 }) => {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await loader({ limit: pageSize, offset: page * pageSize });
      setData(res.data?.data || { rows: [], total: 0 });
    } catch (_) {
      setData({ rows: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [loader, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="qrt-card">
      {loading ? <Spinner /> : (
        <table className="qrt-table">
          <thead>
            <tr>{columns.map((c) => <th key={c} className={c.endsWith(" ") ? "r" : undefined}>{c.trim()}</th>)}</tr>
          </thead>
          <tbody>
            {data.rows.map(renderRow)}
            {data.rows.length === 0 && (
              <tr><td className="empty" colSpan={columns.length}>{empty}</td></tr>
            )}
          </tbody>
        </table>
      )}
      <Pager page={page} total={data.total} pageSize={pageSize} onPage={setPage} />
    </div>
  );
};

const QrtUserDetail = () => {
  const { id } = useParams();
  const userId = parseInt(id, 10);
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [detail, setDetail] = useState(null);
  const [txns, setTxns] = useState({ rows: [], total: 0 });
  const [txnPage, setTxnPage] = useState(0);
  const [adj, setAdj] = useState({ amount: "", reason: "" });
  const [feePrompt, setFeePrompt] = useState(null);
  const [markupDraft, setMarkupDraft] = useState({});
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await qrtGetUser(userId);
      const d = res.data?.data;
      setDetail(d);
      const overrides = {};
      (d?.markup_overrides || []).forEach((m) => { overrides[m.category] = String(m.markup); });
      setMarkupDraft(overrides);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load user");
      navigate("/qrt/users");
    }
  }, [userId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    qrtUserTransactions(userId, { limit: 15, offset: txnPage * 15 })
      .then((res) => setTxns(res.data?.data || { rows: [], total: 0 }))
      .catch(() => {});
  }, [userId, txnPage]);

  const loadCampaigns = useCallback((params) => qrtListCampaigns({ ...params, user_id: userId }), [userId]);
  const loadFlows = useCallback((params) => qrtListFlows({ ...params, user_id: userId }), [userId]);
  const loadTemplates = useCallback((params) => qrtListTemplates({ ...params, user_id: userId }), [userId]);
  const loadMessages = useCallback((params) => qrtListMessages({ ...params, user_id: userId }), [userId]);

  if (!detail) return <Spinner />;
  const { user, wallet, counts, analytics, revenue, current_subscription: currentPlan } = detail;
  const frozen = wallet?.status === "frozen";
  const suspended = user.status === "suspended";

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const applyAdjust = (direction, includePlatformFee) => {
    const amt = Math.abs(Number(adj.amount));
    if (!amt || !adj.reason.trim()) {
      toast.error("Enter a rupee amount and a reason");
      return;
    }
    if (direction === "credit" && includePlatformFee === undefined) {
      setFeePrompt({ amount: amt, reason: adj.reason.trim() });
      return;
    }
    const signed = direction === "debit" ? -amt : amt;
    setFeePrompt(null);
    run(async () => {
      await qrtAdjustWallet(userId, signed, adj.reason.trim(), {
        includePlatformFee: direction === "credit" ? includePlatformFee !== false : undefined,
      });
      setAdj({ amount: "", reason: "" });
      setTxnPage(0);
    }, direction === "debit" ? `₹${amt} debited` : `₹${amt} credited`);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    run(async () => {
      const payload = { name: edit.name, email: edit.email, phone: edit.phone };
      if (edit.password) payload.new_password = edit.password;
      await qrtUpdateUser(userId, payload);
      setEdit(null);
    }, "User updated");
  };

  return (
    <div>
      <div className="qrt-page-head">
        <button type="button" className="camp-ico" onClick={() => navigate("/qrt/users")} aria-label="Back">
          <ArrowLeft size={16} />
        </button>
        <div>
          <h2>{user.name}</h2>
          <p>
            {user.email} · #{user.id} · joined {fmtDateTime(user.created_at)}
            {" · "}
            {user.partner_id
              ? <span className="ping-chip p">{user.partner_name || `Partner #${user.partner_id}`}</span>
              : <span className="ping-chip n">Blutec Ping</span>}
            {user.app_domain ? ` · ${user.app_domain}` : ""}
            {" · "}
            {currentPlan ? (
              <span className="ping-chip g">{currentPlan.plan_display_name}</span>
            ) : (
              <span className="ping-chip n">No plan</span>
            )}
          </p>
        </div>
        <div className="grow" />
        <StatusChip value={user.status || "active"} />
        <StatusChip value={wallet?.status || "active"} />
        <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => setEdit({
          name: user.name, email: user.email, phone: user.phone || "", password: "",
        })}>
          <Pencil size={14} /> Edit
        </button>
        <button
          type="button"
          className="ping-btn ping-btn-ghost ping-btn-sm"
          disabled={busy}
          onClick={() => run(
            () => qrtSetWalletFrozen(userId, !frozen),
            frozen
              ? "Wallet unfrozen — user can sign in again"
              : "Account frozen — user is logged out immediately"
          )}
        >
          <Snowflake size={14} />
          {frozen ? "Unfreeze" : "Freeze account"}
        </button>
        <button
          type="button"
          className="ping-btn ping-btn-ghost ping-btn-sm"
          disabled={busy}
          onClick={() => run(
            () => qrtSetUserStatus(userId, suspended ? "active" : "suspended"),
            suspended
              ? "User reactivated"
              : "User deactivated — they are logged out immediately"
          )}
        >
          {suspended ? <CheckCircle2 size={14} /> : <Ban size={14} />}
          {suspended ? "Activate" : "Deactivate"}
        </button>
        <button
          type="button"
          className="ping-btn ping-btn-ghost ping-btn-sm"
          disabled={busy}
          onClick={() => {
            if (!window.confirm(`Permanently delete ${user.name}? Only empty accounts can be deleted.`)) return;
            run(async () => {
              await qrtDeleteUser(userId);
              navigate("/qrt/users");
            }, "User deleted");
          }}
        >
          <Trash2 size={14} /> Delete
        </button>
      </div>

      <div className="ping-pill-tabs" style={{ marginBottom: 14 }}>
        {TABS.map((t) => (
          <button key={t.id} type="button" className={`ping-ptab ${tab === t.id ? "on" : ""}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <div className="qrt-stats">
            <StatLike
              label="Current plan"
              value={currentPlan?.plan_display_name || "No plan"}
              sub={currentPlan ? `${periodLabel(currentPlan.billing_period)} · expires ${fmtDateTime(currentPlan.expires_at)}` : "Not subscribed"}
            />
            {user.partner_id ? (
              <>
                <StatLike
                  label="Customer spend"
                  value={fmtMoney(revenue?.revenue)}
                  sub={`settles to ${user.partner_name || "partner"} — not Blutec`}
                />
                <StatLike
                  label="Blutec take"
                  value={fmtMoney(revenue?.blutec_take)}
                  sub={`what ${user.partner_name || "the partner"} paid us for this user's traffic`}
                />
              </>
            ) : (
              <StatLike label="Revenue" value={fmtMoney(revenue?.revenue)} sub={`margin ${fmtMoney(revenue?.margin)}`} />
            )}
            <StatLike label="Recharges" value={fmtMoney(revenue?.recharges)} sub={`${num(revenue?.messages_billed)} msgs billed`} />
            <StatLike label="Wallet" value={fmtMoney(wallet?.balance)} sub={wallet?.status || "active"} />
            <StatLike label="Campaigns" value={num(counts?.campaigns)} sub={`${num(counts?.flows)} flows · ${num(counts?.templates)} templates`} />
            <StatLike label="Sent" value={num(analytics?.sent)} sub={`${num(analytics?.received)} received · ${num(analytics?.failed)} failed`} />
            <StatLike label="Agents" value={num(counts?.agents)} />
          </div>

          {(detail.waba_accounts || []).length > 0 && (
            <div className="qrt-card">
              <div className="qrt-card-head"><h3>WhatsApp numbers</h3></div>
              <table className="qrt-table">
                <thead>
                  <tr><th>Number</th><th>Name</th><th>Phone ID</th><th>WABA</th></tr>
                </thead>
                <tbody>
                  {detail.waba_accounts.map((w) => (
                    <tr key={w.phone_id}>
                      <td className="mono">{w.phone_number}</td>
                      <td>{w.verified_name || "—"}</td>
                      <td className="mono sub">{w.phone_id}</td>
                      <td className="mono sub">{w.waba_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "access" && <AccessSection userId={userId} />}

      {tab === "invoices" && <QrtUserInvoices userId={userId} />}

      {tab === "campaigns" && (
        <UserList
          loader={loadCampaigns}
          empty="No campaigns for this user"
          columns={["Campaign", "Status", "Sent ", "Failed ", "Revenue ", "Created "]}
          renderRow={(c) => (
            <tr key={c.campaign_id}>
              <td>
                <div style={{ fontWeight: 600 }}>{c.campaign_name}</div>
                <div className="sub">#{c.campaign_id} · {c.phone_number || "—"}</div>
              </td>
              <td><StatusChip value={c.status} /></td>
              <td className="r">{num(c.sent)}</td>
              <td className="r">{num(c.failed)}</td>
              <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(c.revenue)}</td>
              <td className="r sub">{fmtDateTime(c.created_at)}</td>
            </tr>
          )}
        />
      )}

      {tab === "flows" && (
        <UserList
          loader={loadFlows}
          empty="No flows for this user"
          columns={["Flow", "Trigger", "State", "Updated ", "Created "]}
          renderRow={(f) => (
            <tr key={f.id}>
              <td>
                <div style={{ fontWeight: 600 }}>{f.name}</div>
                <div className="sub">#{f.id}</div>
              </td>
              <td className="mono">{f.trigger_type || "—"}</td>
              <td><StatusChip value={f.is_active ? "active" : "paused"} /></td>
              <td className="r sub">{fmtDateTime(f.updated_at)}</td>
              <td className="r sub">{fmtDateTime(f.created_at)}</td>
            </tr>
          )}
        />
      )}

      {tab === "templates" && (
        <UserList
          loader={loadTemplates}
          empty="No templates for this user"
          columns={["Template", "Category", "Language", "Status", "Created "]}
          renderRow={(t) => (
            <tr key={t.id}>
              <td className="mono" style={{ fontWeight: 600 }}>{t.name}</td>
              <td className="mono">{t.category || "—"}</td>
              <td className="mono">{t.language || "—"}</td>
              <td><StatusChip value={t.status} /></td>
              <td className="r sub">{fmtDateTime(t.created_at)}</td>
            </tr>
          )}
        />
      )}

      {tab === "messages" && (
        <UserList
          loader={loadMessages}
          pageSize={50}
          empty="No messages for this user"
          columns={["Direction", "Customer", "Type", "Preview", "Campaign", "Time "]}
          renderRow={(msg) => (
            <tr key={msg.id}>
              <td><StatusChip value={msg.direction} /></td>
              <td>
                <div>{msg.customer_name || "—"}</div>
                <div className="sub mono">{msg.customer_wa_id}</div>
              </td>
              <td className="mono">{msg.message_type}</td>
              <td className="sub" style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {msg.template_name ? `[${msg.template_name}] ` : ""}{msg.message_preview || "—"}
              </td>
              <td className="mono sub">{msg.campaign_id || "—"}</td>
              <td className="r sub">{fmtDateTime(msg.created_at)}</td>
            </tr>
          )}
        />
      )}

      {tab === "billing" && (
        <>
          <div className="qrt-card">
            <div className="qrt-card-head"><h3>Wallet adjustment</h3></div>
            <div className="qrt-card-body">
              <p className="sub" style={{ margin: "0 0 10px", fontSize: 13 }}>
                Enter the wallet amount, then <b>Credit</b> or <b>Debit</b>. Reason is required.
                A credit invoice always adds 18% GST. Credit asks whether to add the 2% platform fee and GST on that fee.
              </p>
              <div className="qrt-inline">
                <input type="number" min="0" step="0.01" placeholder="Amount (₹)" value={adj.amount}
                  onChange={(e) => setAdj((a) => ({ ...a, amount: e.target.value }))} style={{ width: 140 }} />
                <input type="text" placeholder="Reason (mandatory, audit-logged)" value={adj.reason}
                  onChange={(e) => setAdj((a) => ({ ...a, reason: e.target.value }))} style={{ flex: 1, minWidth: 180 }} />
                <button type="button" className="ping-btn ping-btn-green ping-btn-sm" disabled={busy}
                  onClick={() => applyAdjust("credit")}>
                  Credit
                </button>
                <button type="button" className="ping-btn ping-btn-danger ping-btn-sm" disabled={busy}
                  onClick={() => applyAdjust("debit")}>
                  Debit
                </button>
              </div>
            </div>
          </div>

          <div className="qrt-card">
            <div className="qrt-card-head">
              <h3>Markup overrides (₹ added on top of our cost — not the customer rate)</h3>
            </div>
            <div className="qrt-card-body">
              <p className="sub" style={{ margin: "0 0 12px", fontSize: 13 }}>
                Customer pays <b>base cost + this markup</b>. India utility cost is ₹0.115 — enter <b>0.045</b> to charge them ₹0.16.
              </p>
              <div className="qrt-mkgrid">
                {CATEGORIES.map((c) => (
                  <div className="row" key={c}>
                    <span>{CATEGORY_LABELS[c]}</span>
                    <input type="number" step="0.01" min="0" placeholder="default"
                      value={markupDraft[c] ?? ""}
                      onChange={(e) => setMarkupDraft((d) => ({ ...d, [c]: e.target.value }))} />
                  </div>
                ))}
              </div>
              <button type="button" className="ping-btn ping-btn-green ping-btn-sm" style={{ marginTop: 10 }}
                disabled={busy}
                onClick={() => run(async () => {
                  const payload = {};
                  CATEGORIES.forEach((c) => {
                    payload[c] = markupDraft[c] === "" || markupDraft[c] === undefined ? null : Number(markupDraft[c]);
                  });
                  await qrtSetUserMarkups(userId, payload);
                }, "Markup overrides saved")}>
                Save overrides
              </button>
            </div>
          </div>

          <div className="qrt-card">
            <div className="qrt-card-head"><h3>Ledger</h3></div>
            <table className="qrt-table">
              <thead>
                <tr><th>Type</th><th className="r">Amount</th><th className="r">Balance after</th><th className="r">When</th></tr>
              </thead>
              <tbody>
                {txns.rows.map((t) => (
                  <tr key={t.id}>
                    <td className="mono">{t.type}</td>
                    <td className="r" style={{ fontWeight: 700 }}>
                      {isCredit(t.type, parseMeta(t.meta), t.sign) ? "+" : "−"}{fmtMoney(t.amount)}
                    </td>
                    <td className="r sub">{fmtMoney(t.balance_after)}</td>
                    <td className="r sub">{fmtDateTime(t.created_at)}</td>
                  </tr>
                ))}
                {txns.rows.length === 0 && (
                  <tr><td className="empty" colSpan={4}>No transactions</td></tr>
                )}
              </tbody>
            </table>
            <Pager page={txnPage} total={txns.total} pageSize={15} onPage={setTxnPage} />
          </div>
        </>
      )}

      {feePrompt && (
        <FeeChoice
          amount={feePrompt.amount}
          busy={busy}
          onCancel={() => setFeePrompt(null)}
          onChoose={(includePlatformFee) => applyAdjust("credit", includePlatformFee)}
        />
      )}

      {edit && (
        <>
          <div className="ping-drawer-backdrop" onClick={() => setEdit(null)} />
          <aside className="ping-drawer" role="dialog">
            <form className="qrt-form" onSubmit={saveEdit} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <div className="ping-drawer-head">
                <div><h3>Edit user</h3><p>#{user.id}</p></div>
                <button type="button" className="camp-ico" onClick={() => setEdit(null)} aria-label="Close">×</button>
              </div>
              <div className="ping-drawer-body qrt-form">
                <label>Name<input value={edit.name} onChange={(e) => setEdit((f) => ({ ...f, name: e.target.value }))} required /></label>
                <label>Email<input type="email" value={edit.email} onChange={(e) => setEdit((f) => ({ ...f, email: e.target.value }))} required /></label>
                <label>Phone<input value={edit.phone} onChange={(e) => setEdit((f) => ({ ...f, phone: e.target.value }))} /></label>
                <label>Reset password<input type="password" value={edit.password} onChange={(e) => setEdit((f) => ({ ...f, password: e.target.value }))} placeholder="Leave blank to keep" /></label>
              </div>
              <div className="ping-drawer-foot">
                <button type="button" className="ping-btn ping-btn-ghost" onClick={() => setEdit(null)}>Cancel</button>
                <button type="submit" className="ping-btn ping-btn-green" disabled={busy}>
                  {busy && <Loader2 size={14} className="animate-spin" />} Save
                </button>
              </div>
            </form>
          </aside>
        </>
      )}
    </div>
  );
};

const StatLike = ({ label, value, sub }) => (
  <div className="qrt-stat">
    <p className="qrt-stat-label">{label}</p>
    <p className="qrt-stat-value">{value}</p>
    {sub && <p className="qrt-stat-sub">{sub}</p>}
  </div>
);

export default QrtUserDetail;
